import { vec4 } from 'gl-matrix'
import GLArrayBuffer from 'nanogl/arraybuffer'
import Fbo from 'nanogl/fbo'
import Program from 'nanogl/program'
import Texture from 'nanogl/texture'
import { GLContext, isWebgl2 } from 'nanogl/types'
import PixelFormats from 'nanogl-pf'

import { compileProgram } from '@/webgl/core/CreateProgram'
import MsaaFbo from '@/webgl/postprocess/MsaaFbo'

import { EffectDependency } from './effects/base-effect'
import BaseEffect from './effects/base-effect'
import main_frag from './glsl/main.frag'
import main_vert from './glsl/main.vert'

interface PostProcessOptions {
  msaaSamples: number
  mipmap: boolean
  alpha: boolean
  hdr: boolean
}

export default class Post {
  clearColor = vec4.fromValues(1, 0.957, 0.898, 1)
  gl: GLContext

  _effects: BaseEffect[]
  _flags: EffectDependency
  _shaderInvalid: boolean

  renderWidth: number
  renderHeight: number
  bufferWidth: number
  bufferHeight: number

  enabled: boolean

  hasDepthTexture: boolean

  msaaSamples: number = 0
  mipmap: boolean = false
  alpha: boolean = false
  hdr: boolean = false

  msaaFbo: MsaaFbo
  mainFbo: Fbo
  mainColor: Texture

  prg: Program
  fsPlane: GLArrayBuffer

  constructor(gl: GLContext, opts: Partial<PostProcessOptions>) {
    this.gl = gl

    this._effects = []
    this._flags = 0
    this._shaderInvalid = true

    this.renderWidth = 1
    this.renderHeight = 1

    this.bufferWidth = 1
    this.bufferHeight = 1

    this.enabled = true

    this.msaaSamples = opts.msaaSamples || 0
    this.mipmap = opts.mipmap || false
    this.alpha = opts.alpha || false
    this.hdr = opts.hdr || false

    this.hasDepthTexture = PixelFormats.getInstance(gl).hasDepthTexture()

    this.msaaFbo = this.genFbo()

    this.mainFbo = this.msaaFbo.blitFbo!
    this.mainColor = this.mainFbo.getColor(0) as Texture
    this.mainColor.setFilter(true, false, false)

    this.prg = new Program(gl)

    const fsData = new Float32Array([0, 0, 1, 0, 0, 1, 1, 1])
    this.fsPlane = new GLArrayBuffer(gl, fsData)
    this.fsPlane.attrib('aTexCoord0', 2, gl.FLOAT)
  }

  genFbo(): MsaaFbo {
    const gl = this.gl
    const pf = PixelFormats.getInstance(gl)

    const floatAlpha = [
      pf.RGBA16F,
      // pf.RGBA32F,
      pf.RGBA8,
      pf.RGB8,
    ]

    const floatOpaque = [
      pf.RGB16F,
      // pf.RGB32F,
      pf.RGB8,
    ]

    const alpha = [pf.RGBA8]

    const opaque = [pf.RGB8]

    const configs = []
    if (this.alpha && this.hdr) configs.push(...floatAlpha)
    if (!this.alpha && this.hdr) configs.push(...floatOpaque)
    if (this.alpha && !this.hdr) configs.push(...alpha)
    if (!this.alpha && !this.hdr) configs.push(...opaque)

    // contain RGB8  so cfg can't be null
    const cfg = pf.getRenderableFormat(configs)!

    const msaaFbo = new MsaaFbo(gl, cfg, this.msaaSamples)

    return msaaFbo
  }

  regenerateFbo() {
    this.msaaFbo = this.genFbo()
    this.mainFbo = this.msaaFbo.blitFbo!
    this.mainColor = this.mainFbo.getColor(0) as Texture
    this.mainColor.setFilter(true, false, false)
    this.resize(this.bufferWidth, this.bufferHeight)
  }

  dispose() {
    this.mainFbo.dispose()
    this.fsPlane.dispose()
    this.prg.dispose()
  }

  _needDepth(): boolean {
    return (this._flags & EffectDependency.DEPTH) !== 0
  }

  _needLinear(): boolean {
    return (this._flags & EffectDependency.LINEAR) !== 0
  }

  add(effect: BaseEffect) {
    if (this._effects.indexOf(effect) === -1) {
      this._effects.push(effect)
      effect._init(this)
      effect.resize(this.renderWidth, this.renderHeight)
      this._flags |= effect._flags
      this._shaderInvalid = true
    }
  }

  prepend(effect: BaseEffect) {
    if (this._effects.indexOf(effect) === -1) {
      this._effects.unshift(effect)
      effect._init(this)
      effect.resize(this.renderWidth, this.renderHeight)
      this._flags |= effect._flags
      this._shaderInvalid = true
    }
  }

  remove(effect: BaseEffect) {
    const i = this._effects.indexOf(effect)
    if (i > -1) {
      this._effects.splice(i, 1)
      effect.release()
      effect.post = null
      this._shaderInvalid = true

      if (effect._flags !== 0) {
        this._flags = 0
        for (let j = 0; j < this._effects.length; j++) {
          this._flags |= effect._flags
        }
      }
    }
  }

  resize(w: number, h: number) {
    this.bufferWidth = w
    this.bufferHeight = h

    // this.mainFbo.resize(this.bufferWidth, this.bufferHeight);
    this.msaaFbo.setSize(this.bufferWidth, this.bufferHeight)

    for (let i = 0; i < this._effects.length; i++) {
      this._effects[i].resize(w, h)
    }
  }

  preRender(w: number, h: number) {
    this.renderWidth = w
    this.renderHeight = h

    if (this.enabled) {
      const bufferWidth = this.mipmap ? nextPOT(w) : w
      const bufferHeight = this.mipmap ? nextPOT(h) : h

      if (this.bufferWidth !== bufferWidth || this.bufferHeight !== bufferHeight) {
        this.resize(bufferWidth, bufferHeight)
      }
    }
  }

  needDepthPass() {
    return this.enabled && this._needDepth() && !this.hasDepthTexture
  }

  bindColor() {
    const gl = this.gl

    const c = this.clearColor

    if (this.enabled) {
      this.msaaFbo.renderFbo!.bind()
      gl.clearColor(c[0], c[1], c[2], c[3])
      // gl.viewport(0, 0, this.renderWidth, this.renderHeight);
      // gl.viewport(0, 0, this.msaaFbo._width, this.msaaFbo._height);
      this.msaaFbo.defaultViewport()
      this.msaaFbo.renderFbo!.clear()
    } else {
      gl.clearColor(c[0], c[1], c[2], c[3])
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT)
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      gl.viewport(0, 0, this.renderWidth, this.renderHeight)
    }
  }

  render(fbo: Fbo = null) {
    if (!this.enabled) {
      return
    }

    const gl = this.gl
    this.msaaFbo.blitMsaa()

    for (let i = 0; i < this._effects.length; i++) {
      this._effects[i].preRender()
    }

    if (fbo) {
      fbo.bind()
      fbo.defaultViewport()
    } else {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      gl.viewport(0, 0, this.renderWidth, this.renderHeight)
      gl.clearColor(0.0, 0.0, 0.0, 1.0)
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
    }

    if (this._shaderInvalid) {
      this.buildProgram()
    }

    this.prg.use()

    for (let i = 0; i < this._effects.length; i++) {
      this._effects[i].setupProgram(this.prg)
    }

    this.prg.tInput(this.mainColor)

    if (this._needDepth()) {
      if (this.hasDepthTexture) this.prg.tDepth(this.mainFbo.getDepth())
      else throw 'no depth texture'
    }

    this.fillScreen(this.prg)
  }

  fillScreen(prg: Program, fullframe: boolean = false) {
    if (fullframe === true) {
      prg.uViewportScale(1, 1)
    } else {
      prg.uViewportScale(this.renderWidth / this.bufferWidth, this.renderHeight / this.bufferHeight)
    }

    this.fsPlane.attribPointer(prg)
    this.fsPlane.drawTriangleStrip()
  }

  buildProgram() {
    const codeList: string[] = [],
      precodeList: string[] = []

    const effects = this._effects
    for (let i = 0; i < effects.length; i++) {
      effects[i].genCode(precodeList, codeList)
    }

    const code = codeList.join('\n')
    const precode = precodeList.join('\n')

    const frag = main_frag({
      code: code,
      precode: precode,
    })

    const vert = main_vert()

    const depthTex = this._needDepth() && this.hasDepthTexture
    let defs = ''

    if (isWebgl2(this.gl)) {
      // webgl2
      defs += '#version 300 es\n'
    }

    defs += 'precision highp float;\n'
    defs += '#define NEED_DEPTH ' + +this._needDepth() + '\n'
    defs += '#define TEXTURE_DEPTH ' + +depthTex + '\n'

    compileProgram(this.gl, this.prg, vert, frag, defs, 'post')

    this._shaderInvalid = false

    this.mainColor.bind()
    this.mainColor.setFilter(this._needLinear(), this.mipmap, false)
  }
}

// ----------------
// utilities
// ----------------

const MAX_POT = 4096

function nextPOT(n: number): number {
  let p = 1

  while (p < n) p <<= 1

  if (p > MAX_POT) p = MAX_POT

  return p
}
